import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface MobileSheetProps {
  /** Sheet visível. Diferente do `Modal`, o componente fica montado sempre — ver nota abaixo. */
  aberto: boolean;
  /** Nome acessível do diálogo. O consumidor mantém seu próprio título visível dentro de `children`. */
  titulo: string;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Bottom sheet touch-first — contraparte mobile do `Modal` (que é diálogo
 * desktop via Radix). Possui backdrop, clique-fora, Escape, scroll lock,
 * safe-area e, principalmente, a geometria do teclado virtual: consome
 * `--kb-inset` e `--vv-height` publicadas por `useTecladoVirtual`, para
 * que o conteúdo nunca fique debaixo do teclado.
 *
 * Recebe `aberto` como prop em vez de seguir a convenção mount/unmount do
 * `Modal`: aqui a animação de saída importa, e `AnimatePresence` exige que
 * o componente continue montado enquanto ela roda.
 *
 * Não estiliza o conteúdo — isso é responsabilidade de `children`, como no
 * `Modal`. O `max-height` do card só produz o efeito pretendido se
 * `children` incluir um filho com `overflow` diferente de `visible`
 * (tipicamente `flex-1 min-h-0 overflow-y-auto`) — sem isso, o conteúdo
 * simplesmente transborda para fora do card em vez de rolar.
 */
export function MobileSheet({ aberto, titulo, onClose, children }: MobileSheetProps) {
  useEffect(() => {
    if (!aberto) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [aberto, onClose]);

  return (
    <AnimatePresence>
      {aberto && (
        <motion.div
          className="fixed inset-0 z-40 flex flex-col justify-end"
          role="dialog"
          aria-modal="true"
          aria-label={titulo}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            data-testid="mobile-sheet-backdrop"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative mx-auto flex w-full max-w-md flex-col rounded-t-3xl border-t border-white/[0.06] bg-jukebox-sheet"
            style={{
              // Nunca mais alto que a área realmente visível — com o teclado
              // aberto, `--vv-height` já reflete a altura reduzida.
              maxHeight: 'min(calc(var(--vv-height, 100dvh) * 0.85), calc(var(--vv-height, 100dvh) - 1.5rem))',
              // Empurra o conteúdo para cima do teclado; sem teclado, respeita
              // a safe area do aparelho.
              paddingBottom: 'max(var(--kb-inset, 0px), env(safe-area-inset-bottom, 0px))',
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <div className="flex shrink-0 justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-white/10" />
            </div>

            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
